import { DayOfWeek, TractionType } from "./entities";

type JsonObject = { [key: string]: any };

const tractionHumanMap = new Map<TractionType, string>([
  [TractionType.Tram, "Tramvaje"],
  [TractionType.Trolleybus, "Trolejbusy"],
  [TractionType.Bus, "Autobusy"]
]);

const idTractionMap = new Map<number, TractionType>([
  [1, TractionType.Tram],
  [2, TractionType.Trolleybus],
  [3, TractionType.Bus]
]);

const tractionSAMap = new Map<TractionType, number>([
  [TractionType.Tram, 1],
  [TractionType.Trolleybus, 3],
  [TractionType.Bus, 5]
]);

export function getRegistrationNumber(id: string) {
  return Number(id.slice(1));
}

export function getTractionType(id: string) {
  return idToTraction(Number(id.charAt(0)));
}

export function uppercaseFirst(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function idToTraction(value: number) {
  return idTractionMap.get(value);
}

export function tractionToHuman(type: TractionType) {
  return tractionHumanMap.get(type);
}

export function tractionToSA(type: TractionType) {
  return tractionSAMap.get(type);
}

export function pascalToCamel(obj: JsonObject): JsonObject {
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => pascalToCamel(item));
  }

  return Object.keys(obj).reduce((acc: JsonObject, key: string) => {
    let camelKey = key.charAt(0).toLowerCase() + key.slice(1);
    acc[camelKey] = pascalToCamel(obj[key]);
    return acc;
  }, {});
}

export function fleetoraAddProperties(obj: JsonObject) {
  for (const innerObj of obj as any[]) {
    innerObj["registrationNumber"] = getRegistrationNumber(innerObj["id"]);
    innerObj["tractionType"] = getTractionType(innerObj["id"]);
  }

  return obj;
}

export function fleetoraRewriteProperties(obj: JsonObject): JsonObject {
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  let renameMap: { [key: string]: string } = {
    vehicleNumber: "id",
    delayMin: "delayMinutes"
  };

  if (Array.isArray(obj)) {
    return obj.map((item) => fleetoraRewriteProperties(item));
  }

  return Object.keys(obj).reduce((acc: JsonObject, key: string) => {
    let newKey = renameMap[key] || key;
    acc[newKey] = fleetoraRewriteProperties(obj[key]);
    return acc;
  }, {});
}

export function ageToHuman(seconds: number) {
  if (seconds < 60) {
    return [seconds, "s"];
  } else if (seconds < 3600) {
    let minutes = Math.floor(seconds / 60);
    return [minutes, "min."];
  } else if (seconds < 86400) {
    let hours = Math.floor(seconds / 3600);
    return [hours, "hod."];
  } else {
    let days = Math.floor(seconds / 86400);
    return [days, days == 1 ? "den" : days >= 2 && days <= 4 ? "dny" : "dnů"];
  }
}

const secondsInMinute = 60;
const secondsInHour = 60 * secondsInMinute;
const secondsInDay = 24 * secondsInHour;
const secondsInMonth = 30 * secondsInDay;
const secondsInYear = 365 * secondsInDay;

export function photoAgeToHuman(seconds: number): [number, string] {
  if (seconds >= secondsInYear) {
    const years = Math.floor(seconds / secondsInYear);
    return [years, years === 1 ? "rokem" : years >= 2 && years <= 4 ? "roky" : "lety"];
  } else if (seconds >= secondsInMonth) {
    const months = Math.floor(seconds / secondsInMonth);
    return [months, months === 1 ? "měsícem" : "měsíci"];
  } else if (seconds >= secondsInDay) {
    const days = Math.floor(seconds / secondsInDay);
    return [days, days === 1 ? "dnem" : "dny"];
  } else if (seconds >= secondsInHour) {
    const hours = Math.floor(seconds / secondsInHour);
    return [hours, hours === 1 ? "hodinou" : "hodinami"];
  } else if (seconds >= secondsInMinute) {
    const minutes = Math.floor(seconds / secondsInMinute);
    return [minutes, minutes === 1 ? "minutou" : "minutami"];
  }
  return [seconds, seconds === 1 ? "sekundou" : "sekundami"];
}

export function toDayOfWeek(day: string | null): DayOfWeek | null {
  if (!day) return null;

  const normalizedDay = day.trim().toLowerCase();
  for (const dayEnumValue of Object.values(DayOfWeek)) {
    if (dayEnumValue.toLowerCase() === normalizedDay) {
      return dayEnumValue as DayOfWeek;
    }
  }

  return null;
}

export function getHhMm(timeSpan: string) {
  return timeSpan.slice(0, 5);
}

export function offsetMidnight(timeSpan: string) {
  const [hours, minutes] = timeSpan.split(":").map(Number);

  if (hours >= 24) {
    return `${hours - 24}:${String(minutes).padStart(2, "0")}`;
  }

  return timeSpan;
}
